import type { ReactNode } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useIsAdmin } from '@/hooks/useIsAdmin'
import type { WishlistEntryWithGame, Friend, CurrentUser } from '@/types'
import { GameCover } from '@/components/ui/GameCover'
import { Avatar } from '@/components/ui/Avatar'
import { VoteButtons } from '@/components/ui/VoteButtons'
import { PriceTag, formatPrice } from '@/components/ui/PriceTag'
import { RatingPill } from '@/components/ui/RatingPill'
import { CoopModesBadges, CoopPlayersBadge } from '@/components/ui/CoopBadges'
import { HltbBadge, HltbTimePill } from '@/components/ui/HltbBadge'
import { Icon } from '@/components/ui/Icon'
import { ItadTooltip } from '@/components/ui/ItadTooltip'
import { clearGameCache } from '@/api/games'

interface WishlistRowProps {
  entry: WishlistEntryWithGame
  ownership: Record<string, string[]>
  groupMembers: Friend[]
  currentUser: CurrentUser
  addedByUser: Friend | undefined
  isExpanded: boolean
  onToggleExpand: () => void
  onVote: (vote: 1 | -1 | 0) => void
  onRemove: () => void
  onViewDetails: () => void
  onPromoteToPlaying?: () => void
  onPromoteToPlayed?: () => void
  steamWishlistedBy?: string[]
  isOnDeck?: boolean
  onToggleOnDeck?: () => void
  dragHandle?: ReactNode
  canEdit?: boolean
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}

export function WishlistRow({
  entry,
  ownership,
  groupMembers,
  currentUser,
  addedByUser,
  isExpanded,
  onToggleExpand,
  onVote,
  onRemove,
  onViewDetails,
  onPromoteToPlaying,
  onPromoteToPlayed,
  steamWishlistedBy = [],
  isOnDeck = false,
  onToggleOnDeck,
  dragHandle,
  canEdit = true,
}: WishlistRowProps) {
  const { game } = entry
  const owners = ownership[entry.gameId] ?? []
  const steamWishMembers = steamWishlistedBy
    .map((id) => groupMembers.find((m) => m.id === id))
    .filter((m): m is Friend => m !== undefined)
  const steamWishNames = steamWishMembers.map((m) => m.nickname ?? m.name).join(', ')

  const isAdmin = useIsAdmin()
  const queryClient = useQueryClient()
  const clearCacheMutation = useMutation({
    mutationFn: () => clearGameCache(game.id),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['wishlist'] }),
  })
  const netVotes = Object.values(entry.votes).reduce((sum, v) => sum + v, 0)
  const currentPrice = game.sale ?? game.price
  const isAllTimeLow = game.price > 0 && game.historicalLow != null && currentPrice <= game.historicalLow

  return (
    <div className={`cg-row${isExpanded ? ' open' : ''}${isOnDeck ? ' on-deck' : ''}`}>
      {/* Collapsed row */}
      <div className="cg-row-main" onClick={onToggleExpand}>
        {dragHandle}
        <div className="cg-col-cover">
          <GameCover game={game} size="sm" />
        </div>

        <div className="cg-col-title">
          <div className="cg-title-line">
            <span className="cg-title">{game.title}</span>
            {netVotes !== 0 && (
              <span className={netVotes > 0 ? 'cg-score-positive' : 'cg-score-negative'}>
                {netVotes > 0 ? `+${netVotes}` : netVotes}
              </span>
            )}
          </div>
          <div className="cg-subtitle">
            {addedByUser && <span className="cg-added-name">{addedByUser.nickname ?? addedByUser.name}</span>}
            {entry.addedAt && (
              <>
                <span className="cg-dot">·</span>
                <span>{relativeTime(entry.addedAt)}</span>
              </>
            )}
            {game.tags.slice(0, 2).map((tag) => (
              <span key={tag}>
                <span className="cg-dot">·</span>
                <span className="cg-tags-inline">{tag}</span>
              </span>
            ))}
          </div>
          <div className="cg-coop compact">
            <CoopPlayersBadge game={game} groupSize={groupMembers.length} />
            <HltbBadge game={game} />
            <CoopModesBadges coop={game.coop} compact />
          </div>
        </div>

        <div className="cg-col-rating">
          {game.rating > 0 && <RatingPill rating={game.rating} reviews={game.reviews} />}
          {steamWishMembers.length > 0 && (
            <span
              className="cg-steam-wish-badge"
              title={`On personal Steam wishlists: ${steamWishNames}`}
            >
              <Icon name="wishlist" size={11} />
              {steamWishMembers.length}
            </span>
          )}
        </div>

        <div className="cg-col-owners">
          <div className="cg-owner-stack">
            {groupMembers.map((member) => (
              <Avatar
                key={member.id}
                friend={member}
                size={22}
                owns={owners.includes(member.id) ? true : member.status === 'limited' ? null : false}
                showRing={true}
              />
            ))}
          </div>
          {groupMembers.length > 0 && (
            <span className="cg-owner-count">
              {owners.length}/{groupMembers.length} own
            </span>
          )}
        </div>

        <div className="cg-col-votes" onClick={(e) => e.stopPropagation()}>
          <div className="cg-mobile-price-combined">
            <PriceTag game={game} compact />
          </div>
          <VoteButtons
            votes={entry.votes}
            onVote={onVote}
            currentUserId={currentUser.steamId}
          />
        </div>

        <div className="cg-col-price">
          <div className="cg-price-with-low">
            <PriceTag game={game} />
            {isAllTimeLow && (
              <ItadTooltip gameId={game.id} currency={game.currency}>
                <span
                  className="cg-atl-badge"
                  title={`Matches ITAD all-time low: ${formatPrice(game.historicalLow!, game.currency)}`}
                >
                  <Icon name="tag" size={11} />
                  ATL
                </span>
              </ItadTooltip>
            )}
          </div>
        </div>

        <div className="cg-row-end" onClick={(e) => e.stopPropagation()}>
          {canEdit && onToggleOnDeck && (
            <button
              type="button"
              className="cg-ondeck-toggle"
              data-active={isOnDeck}
              onClick={onToggleOnDeck}
              title={isOnDeck ? 'Remove from On Deck' : 'Add to On Deck'}
              aria-pressed={isOnDeck}
            >
              <Icon name="bookmark" size={14} />
            </button>
          )}
          <button
            type="button"
            className="cg-row-expand"
            onClick={onToggleExpand}
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            <Icon name={isExpanded ? 'collapse' : 'expand'} size={14} />
          </button>
        </div>
      </div>

      {/* Expanded detail panel — animate real height so the row, the On Deck
          box, and sibling rows all grow through natural flow (no scale, which
          would distort content and mismatch nested layout animations) */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            className="cg-row-detail-wrap"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { type: 'spring', stiffness: 400, damping: 38 },
              opacity: { duration: 0.15 },
            }}
            style={{ overflow: 'hidden' }}
          >
            <div className="cg-row-detail">
          <div className="cg-detail-cover">
            <GameCover game={game} size="lg" />
            <button
              className="cg-btn ghost"
              onClick={onViewDetails}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            >
              <Icon name="eye" size={12} /> View details
            </button>
            <a
              href={`https://store.steampowered.com/app/${game.appId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="cg-btn"
              style={{ fontSize: 12, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            >
              <img
                src="https://store.steampowered.com/favicon.ico"
                alt=""
                style={{ width: 14, height: 14 }}
              />
              View on Steam
            </a>
          </div>

          <div>
            <p className="cg-detail-desc">{game.desc}</p>

            <div className="cg-detail-meta">
              {game.released && (
                <div className="cg-meta-row">
                  <span className="cg-meta-label">Released</span>
                  <span>{game.released}</span>
                </div>
              )}
              {game.developer && (
                <div className="cg-meta-row">
                  <span className="cg-meta-label">Developer</span>
                  <span>{game.developer}</span>
                </div>
              )}
              {game.multiplayer && (
                <div className="cg-meta-row">
                  <span className="cg-meta-label">Multiplayer</span>
                  <span>{game.multiplayer}</span>
                </div>
              )}
              {game.price > 0 && game.historicalLow != null && (
                <div className="cg-meta-row">
                  <span className="cg-meta-label">ITAD all-time low</span>
                  <ItadTooltip gameId={game.id} currency={game.currency}>
                    <span className="cg-itad-low-value">
                      {formatPrice(game.historicalLow, game.currency)}
                    </span>
                  </ItadTooltip>
                </div>
              )}
              {game.hltbTimes && (
                <div className="cg-meta-row">
                  <span className="cg-meta-label">How Long to Beat</span>
                  <div className="cg-hltb-times">
                    {game.hltbTimes.mainStory && (
                      <HltbTimePill label="Main Story" hours={game.hltbTimes.mainStory} url={game.hltbTimes.url} />
                    )}
                    {game.hltbTimes.mainExtra && (
                      <HltbTimePill label="Main + Extras" hours={game.hltbTimes.mainExtra} url={game.hltbTimes.url} primary />
                    )}
                    {game.hltbTimes.completionist && (
                      <HltbTimePill label="Completionist" hours={game.hltbTimes.completionist} url={game.hltbTimes.url} />
                    )}
                  </div>
                </div>
              )}
              <div className="cg-meta-row">
                <span className="cg-meta-label">Tags</span>
                <div className="cg-detail-tags">
                  {game.tags.map((tag) => (
                    <span key={tag} className="cg-detail-tag">{tag}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Ownership grid */}
            <div className="cg-ownership-detail">
              <div className="cg-ownership-title">Who owns it</div>
              <div className="cg-ownership-grid">
                {groupMembers.map((member) => {
                  const ownsIt = owners.includes(member.id)
                  const unknown = member.status === 'limited'
                  return (
                    <div
                      key={member.id}
                      className="cg-ownership-cell"
                      data-owns={ownsIt ? 'true' : 'false'}
                    >
                      <Avatar friend={member} size={24} owns={unknown ? null : ownsIt} showRing={true} />
                      <div className="cg-ownership-cell-text">
                        <span className="cg-ownership-name">{member.nickname ?? member.name}</span>
                        <span className="cg-ownership-status">
                          {unknown ? 'Unknown' : ownsIt ? 'Owns' : "Doesn't own"}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Votes grid */}
            <div className="cg-votes-detail">
              <div className="cg-votes-title">Votes</div>
              <div className="cg-votes-grid">
                {groupMembers.map((member) => {
                  const v = entry.votes[member.id]
                  const voteType = v === 1 ? 'up' : v === -1 ? 'down' : 'none'
                  const icon = v === 1 ? '▲' : v === -1 ? '▼' : '—'
                  const isMe = member.id === currentUser.steamId
                  return (
                    <div
                      key={member.id}
                      className={`cg-vote-cell${isMe ? ' me' : ''}`}
                      data-vote={voteType}
                    >
                      <Avatar friend={member} size={20} showRing={false} />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {member.nickname ?? member.name}
                      </span>
                      {isMe ? (
                        <span className="cg-vote-cell-actions">
                          <button
                            type="button"
                            className="cg-vote-cell-btn up"
                            data-active={v === 1}
                            onClick={() => onVote(v === 1 ? 0 : 1)}
                            title="Upvote"
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            className="cg-vote-cell-btn down"
                            data-active={v === -1}
                            onClick={() => onVote(v === -1 ? 0 : -1)}
                            title="Downvote"
                          >
                            ▼
                          </button>
                        </span>
                      ) : (
                        <span className="cg-vote-icon">{icon}</span>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="cg-detail-actions">
              {canEdit && (
                <>
                  <button
                    className="cg-btn cg-btn-primary"
                    data-action="playing"
                    onClick={onPromoteToPlaying}
                    disabled={!onPromoteToPlaying}
                  >
                    <Icon name="controller" size={11} />
                    Start playing
                  </button>
                  {onToggleOnDeck && (
                    <button
                      className="cg-btn"
                      data-action="ondeck"
                      data-active={isOnDeck}
                      onClick={onToggleOnDeck}
                    >
                      <Icon name="bookmark" size={11} />
                      {isOnDeck ? 'Remove from On Deck' : 'Add to On Deck'}
                    </button>
                  )}
                  <button
                    className="cg-btn"
                    data-action="played"
                    onClick={onPromoteToPlayed}
                    disabled={!onPromoteToPlayed}
                  >
                    <Icon name="star" size={11} />
                    Mark played
                  </button>
                  <button
                    className="cg-btn"
                    style={{ color: 'var(--danger)', borderColor: 'oklch(from var(--danger) l c h / 0.4)' }}
                    onClick={onRemove}
                  >
                    Remove from wishlist
                  </button>
                </>
              )}
              {isAdmin && (
                <button
                  className="cg-btn ghost tiny"
                  onClick={() => clearCacheMutation.mutate()}
                  disabled={clearCacheMutation.isPending}
                >
                  {clearCacheMutation.isPending ? 'Clearing…' : 'Clear cache'}
                </button>
              )}
            </div>
            </div>
          </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
